import type {MonthColumn} from "../../utils";
import type {PeriodicViewRow} from "../../types";
import {
    AMOUNT_SKELETON_WIDTHS,
    EmptyTableBody,
    ShimmerBar,
    SkeletonAccountCell,
    SkeletonAmountCell,
} from "../skeletonParts.tsx";
import {PeriodicTableHeader} from "./PeriodicTableHeader.tsx";
import {PeriodicTableRow} from "./PeriodicTableRow.tsx";
import {PeriodicTableShell} from "./PeriodicTableShell.tsx";
import {displayColumnsForTable} from "../../utils";
import {
    COL_SEP_L,
    MONTH_COLUMN_WIDTH,
    STICKY_COLUMN_WIDTH,
    SUMMARY_COLUMN_WIDTH,
} from "./tableStyles.ts";
import type {LegalEntityType} from "../../../../../generated/enums.ts";

interface PeriodicStatementTableProps {
    rows: PeriodicViewRow[];
    monthColumns: MonthColumn[];
    ytdMonthIndex: number;
    currency: string;
    legalEntityType: LegalEntityType;
    onToggleNode: (id: string) => void;
    onAccountClick: (row: PeriodicViewRow) => void;
    onCellClick: (row: PeriodicViewRow, periodIndex: number) => void;
    onKpiClick: (row: PeriodicViewRow) => void;
    loading?: boolean;
    skeletonMonthCount?: number;
    skeletonRowCount?: number;
    emptyMessage?: string;
}

export function PeriodicStatementTable({
    rows,
    monthColumns,
    ytdMonthIndex,
    currency,
    legalEntityType,
    onToggleNode,
    onAccountClick,
    onCellClick,
    onKpiClick,
    loading = false,
    skeletonMonthCount = 12,
    skeletonRowCount = 10,
    emptyMessage,
}: PeriodicStatementTableProps) {
    const displayColumns = displayColumnsForTable(monthColumns, loading, skeletonMonthCount);

    const minWidth =
        STICKY_COLUMN_WIDTH +
        displayColumns.length * MONTH_COLUMN_WIDTH +
        2 * SUMMARY_COLUMN_WIDTH;

    const ytdLabel = monthColumns[ytdMonthIndex]?.shortLabel ?? "";
    const colSpan = 1 + displayColumns.length + 2;

    let body;
    if (loading) {
        body = Array.from({length: skeletonRowCount}, (_, rowIndex) => (
            <tr key={rowIndex} className="[&>*]:border-b [&>*]:border-b-gray-100">
                <SkeletonAccountCell rowIndex={rowIndex} />
                {displayColumns.map((_, colIndex) => (
                    <SkeletonAmountCell
                        key={colIndex}
                        rowIndex={rowIndex}
                        colIndex={colIndex}
                        withLeftBorder={colIndex > 0}
                    />
                ))}
                <td className={`px-3 py-2.5 text-right bg-[#F4FAFE] ${COL_SEP_L}`}>
                    <ShimmerBar
                        className="ml-auto"
                        style={{width: AMOUNT_SKELETON_WIDTHS[rowIndex % AMOUNT_SKELETON_WIDTHS.length]}}
                    />
                </td>
                <td className={`px-3 py-2.5 text-right ${COL_SEP_L}`}>
                    <ShimmerBar
                        className="ml-auto"
                        style={{
                            width: AMOUNT_SKELETON_WIDTHS[(rowIndex + 1) % AMOUNT_SKELETON_WIDTHS.length],
                        }}
                    />
                </td>
            </tr>
        ));
    } else if (rows.length === 0) {
        body = <EmptyTableBody colSpan={colSpan} message={emptyMessage} />;
    } else {
        body = rows.map((row) => (
            <PeriodicTableRow
                key={row.id}
                row={row}
                monthColumns={displayColumns}
                ytdMonthIndex={ytdMonthIndex}
                currency={currency}
                legalEntityType={legalEntityType}
                onToggleNode={onToggleNode}
                onAccountClick={onAccountClick}
                onCellClick={onCellClick}
                onKpiClick={onKpiClick}
            />
        ));
    }

    return (
        <PeriodicTableShell
            loading={loading}
            minWidth={minWidth}
            header={
                <PeriodicTableHeader
                    monthColumns={displayColumns}
                    ytdLabel={ytdLabel}
                    loading={loading}
                    showPlaceholderColumns={loading && monthColumns.length === 0}
                />
            }
            body={body}
        />
    );
}
